import * as React from "react";
import { ViewLarge, Viewport, ViewSmall } from "bernie-view";
import { useLocalization } from "@shared-ui/localization-context";

import { UitkHeading } from "uitk-react-text";
import { UitkLayoutGrid, UitkLayoutGridItem } from "uitk-react-layout-grid";
import { AnalyticsConfig } from "uitk-react-utils";

import SimilarDestinationCard from "./components/SimilarDestinationCard";
import { ItemKeyHelper } from "components/utility/ItemKeyHelper";
import { Action, FlexTrackingInfo, sendDelayedTrackEvent } from "components/utility/analytics/FlexAnalyticsUtils";

import { AspectRatioEnum } from "stores/thumbor/ThumborStore";
import { withStores } from "stores";
import { SimilarDestinationsProps } from "./typings";
import { TeSimilarDestinationFlexModuleResult } from "src/typings/microserviceModels/te-similar-destination-flex-module";
import { IsomorphicCarousel } from "src/components/shared/IsomorphicCarousel/IsomorphicCarousel";

const SimilarDestinations = withStores(
  "analytics",
  "thumbor",
  "flexModuleModelStore"
)((props: SimilarDestinationsProps) => {
  const { flexTemplateNode, flexModuleModelStore, context, thumbor } = props;
  const { id, options } = flexTemplateNode;
  const model = flexModuleModelStore.getModel(id) as TeSimilarDestinationFlexModuleResult | null;
  /* istanbul ignore next */
  if (!model) {
    return null;
  }

  const { content, title } = model;
  const { items: destinations } = content;
  const { formatText } = useLocalization();
  const localizedTitle = title || formatText("similarDestinations.title", context.searchContext.location.name);
  const destinationKeyHelper = new ItemKeyHelper("similar-destinations");

  // This is covered in e2e test
  /* istanbul ignore next */
  const analytics: AnalyticsConfig = {
    id: "TG.LP.Dest.OtherPlaces",
    callback: (moduleId, description) => {
      const flexTrackingInfo: FlexTrackingInfo = {
        moduleName: moduleId,
        action: Action.SCROLL,
        linkName: description,
      };
      sendDelayedTrackEvent(flexTrackingInfo, props.analytics);
    },
  };

  const thumborizedImageUrl = (url: string) => {
    return thumbor.safelyGenerateThumborizedImageUrl(url, 330, 190, context, AspectRatioEnum.DEFAULT, true, 70);
  };

  return (
    <Viewport>
      <ViewSmall>
        <div
          id={id}
          className="SimilarDestinations SimilarDestinations--carousel uitk-cell all-cell-1-1 all-b-margin-six"
          data-fm={options.fmId}
          data-fm-title-id={options.fmTitleId}
        >
          <IsomorphicCarousel
            headline={localizedTitle}
            analytics={analytics}
            carouselName="SimilarDestinations"
            itemsVisible={{ lg: 1, md: 1, sm: 1 }}
            pageBy={1}
            peekWidth={25}
            buttonText={{
              nextButton: formatText("carousel.item.next"),
              prevButton: formatText("carousel.item.prev"),
            }}
          >
            {destinations.map((destination) => (
              <SimilarDestinationCard
                key={destinationKeyHelper.next()}
                destination={destination}
                destinationImageUrl={thumborizedImageUrl(destination.media?.mediaUrl)}
              />
            ))}
          </IsomorphicCarousel>
        </div>
      </ViewSmall>

      <ViewLarge>
        <div
          id={id}
          className="SimilarDestinations uitk-cell all-cell-1-1 all-b-margin-six"
          data-fm={options.fmId}
          data-fm-title-id={options.fmTitleId}
        >
          <UitkHeading size={600} level={3}>
            {localizedTitle}
          </UitkHeading>

          <UitkLayoutGrid columns={4} space="three" className="all-t-margin-three">
            {destinations.map((destination) => (
              <UitkLayoutGridItem key={destinationKeyHelper.next()}>
                <SimilarDestinationCard
                  destination={destination}
                  destinationImageUrl={thumborizedImageUrl(destination.media?.mediaUrl)}
                />
              </UitkLayoutGridItem>
            ))}
          </UitkLayoutGrid>
        </div>
      </ViewLarge>
    </Viewport>
  );
});

export default SimilarDestinations;
